import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { PXMDash } from "@/pages/PXMDash";
import { LoginScreen } from "@/pages/LoginScreen";
import type { Session } from "@supabase/supabase-js";

interface CurrentUser {
  id: string;
  email: string;
  name: string;
  role: string;
  title: string;
}

export function AppRoot() {
  const [session, setSession] = useState<Session | null>(null);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!supabase) {
      console.warn("[PXM] Supabase not configured — skipping auth");
      setChecking(false);
      return;
    }

    // Check for existing session on mount (with timeout so we never hang)
    const sessionTimeout = setTimeout(() => {
      console.warn("[PXM] Session check timed out — proceeding to login");
      setChecking(false);
    }, 8000);

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      clearTimeout(sessionTimeout);
      setSession(s);
      if (s?.user) {
        setCurrentUser({
          id: s.user.id,
          email: s.user.email || "",
          name: s.user.user_metadata?.name || s.user.email?.split("@")[0] || "",
          role: s.user.user_metadata?.role || s.user.app_metadata?.role || "viewer",
          title: s.user.user_metadata?.title || "",
        });
      }
      setChecking(false);
    }).catch((err) => {
      clearTimeout(sessionTimeout);
      console.warn("[PXM] Session check failed:", err);
      setChecking(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user) {
        setCurrentUser({
          id: s.user.id,
          email: s.user.email || "",
          name: s.user.user_metadata?.name || s.user.email?.split("@")[0] || "",
          role: s.user.user_metadata?.role || s.user.app_metadata?.role || "viewer",
          title: s.user.user_metadata?.title || "",
        });
      } else {
        setCurrentUser(null);
      }
    });

    return () => {
      clearTimeout(sessionTimeout);
      subscription.unsubscribe();
    };
  }, []);

  if (checking) {
    return (
      <div className="flex-center bg-bg h-full h-screen">
        <div className="text-center">
          <div className="ff-primary fw-700 text-dark italic mb-5 ls-wider fw-900 text-5xl">PXM DASH</div>
          <div className="text-base text-light">Checking session...</div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <LoginScreen
        onLogin={(s, u) => {
          setSession(s);
          setCurrentUser({
            id: u.id,
            email: u.email || "",
            name: u.user_metadata?.name || u.email?.split("@")[0] || "",
            role: u.user_metadata?.role || u.app_metadata?.role || "viewer",
            title: u.user_metadata?.title || "",
          });
        }}
      />
    );
  }

  const handleUpdateName = async (newName: string) => {
    if (!supabase || !currentUser) return;
    const { error } = await supabase.auth.updateUser({ data: { name: newName } });
    if (!error) {
      setCurrentUser(prev => prev ? { ...prev, name: newName } : prev);
    }
  };

  return (
    <PXMDash
      currentUser={currentUser}
      session={session}
      onUpdateName={handleUpdateName}
      onLogout={async () => {
        await supabase?.auth.signOut();
        setSession(null);
        setCurrentUser(null);
      }}
    />
  );
}
